import type { BackgroundChatStatus } from '~/hooks/queries/use-background-chats';
import type { ChatLightState } from '~/features/chat/chat-light';

/**
 * FRONT-754 — pure decision logic for `inbox-chat-modal.tsx`'s promote-on-
 * close effect, extracted so it's unit-testable without mounting the modal.
 *
 * The bug this fixes: closing a chat only promoted it (surfaced it in the
 * Inspector's monitored-chats list) when its OWN `background_chats` row was
 * still `running` at the moment of close — which misses a chat that's paused
 * on a question/approval/plan (the spine says `needs-you`) even though its
 * last turn already settled.
 *
 * Either signal is sufficient to promote: the bg-chat row is still literally
 * `running` (a normal in-flight turn — and the fallback if the spine query
 * hasn't resolved yet), OR the spine says `working`/`needs-you` (a chat paused
 * on a question/approval the user walked away from).
 *
 * Under the launchpad model (FRONT-634, Mattias's direct decision): a chat
 * that launches a deep research is DONE the moment the research detaches —
 * the research becomes its own first-class Inspector card (own progress,
 * Stop, delivery to the inbox/Files) and has nothing further to do with the
 * chat. So a detached deep research correctly does NOT promote the chat here;
 * since FRONT-787 the chat spine is untouched by research FULL STOP (its last
 * trace, FRONT-694's seen-gated amber pull-back, is gone), so this is now
 * deterministic rather than dependent on a mark-seen write having landed.
 */
export function shouldPromoteOnClose(params: {
  bgRunStatus: BackgroundChatStatus | undefined;
  spineState: ChatLightState | undefined;
}): boolean {
  return (
    params.bgRunStatus === 'running' ||
    params.spineState === 'working' ||
    params.spineState === 'needs-you'
  );
}
